import {Group, Menu, rem, Text, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {adjustFromTimeZoneSafely, DriverActivityEntity, firstOrUndefined, transportType} from "@ots-frontend/ots-data";
import {useCallback} from "react";
import {HasAccess} from "../../../components/auth/HasAccess/HasAccess";
import {toFormDefaultsFromTransport} from "../../TransportFormPage/TransportFormPage";
import {
  DriverActivityTtaMenu
} from "../../../components/driver-activity-grid/DriverActivityTtaMenu/DriverActivityTtaMenu";

export type DriverActivityMenuProps = {
  refetch?: () => void
  context?: DriverActivityEntity[]
}

export const DriverActivityMenu = (props: DriverActivityMenuProps) => {
  const {refetch, context} = props

  const {t} = useTranslation(["translation", "common"]);

  const navigate = useNavigate()

  const onNewShift = useCallback(() => {
    let state: any | undefined = undefined;

    const activity = firstOrUndefined(context)
    if (activity) {
      if (activity.shift) {
        // The shift form starts the driver's next shift where this one ended.
        state = {id: activity.shift.id, copy: true}
      } else if (activity.tta || activity.transport) {
        const transport = activity.tta?.pickupTransport ?? activity.transport
        state = {
          formDefaults: {
            startDate: adjustFromTimeZoneSafely(transport?.createDttm),
            person: transport?.personId,
            vehicle: transport?.vehicleId,
            trailer: transport?.trailerId,
          }
        }
      } else if (activity.dispatch) {
        const dispatch = activity.dispatch
        state = {
          formDefaults: {
            startDate: adjustFromTimeZoneSafely(dispatch?.scheduleDttm),
            person: dispatch?.personId,
            vehicle: dispatch?.vehicleId,
            trailer: dispatch?.trailerId,
          }
        }
      }
    }

    navigate("/shifts/form", {state: state})
  }, [navigate, context])

  const onNewTicket = useCallback(() => {
    let state: any | undefined = undefined;

    const activity = firstOrUndefined(context)
    if (activity) {
      if (activity.shift) {
        const shift = activity.shift
        state = {
          formDefaults: {
            options: {
              regionId: shift?.regionId,
            },
            pickup: {
              date: adjustFromTimeZoneSafely(shift.startDttm),
              person: shift.personId,
              vehicleId: shift.vehicleId,
              trailer: shift.trailerId,
            },
            delivery: {
              date: adjustFromTimeZoneSafely(shift.startDttm),
              person: shift.personId,
              vehicleId: shift.vehicleId,
              trailer: shift.trailerId,
            }
          }
        }
      } else if (activity.tta || activity.transport) {
        const pickupTransport = activity.tta?.pickupTransport ?? activity.pickupTransport
        const deliveryTransport = activity.tta?.deliveryTransport ?? activity.deliveryTransport
        const transport = pickupTransport || deliveryTransport

        state = {
          options: {
            regionId: transport?.regionId,
            category: transport?.category,
            categoryDisabled: typeof transport?.category !== "undefined"
          },
          formDefaults: {
            pickup: toFormDefaultsFromTransport(pickupTransport),
            delivery: toFormDefaultsFromTransport(deliveryTransport),
          }
        }
      } else if (activity.dispatch) {
        const dispatch = activity.dispatch
        state = {
          options: {
            regionId: dispatch.regionId,
            category: dispatch.category,
            categoryDisabled: true
          },
          formDefaults: {
            pickup: {
              date: adjustFromTimeZoneSafely(dispatch.scheduleDttm),
              person: dispatch.personId,
              vehicleId: dispatch.vehicleId,
              trailer: dispatch.trailerId,
              siteCompany: dispatch.pickupSiteCompanyId,
              site: dispatch.pickupSiteId,
              well: dispatch.pickupWellId,
              container: dispatch.pickupContainerId,
              category: dispatch.category,
            },
            delivery: {
              date: adjustFromTimeZoneSafely(dispatch.scheduleDttm),
              person: dispatch.personId,
              vehicleId: dispatch.vehicleId,
              trailer: dispatch.trailerId,
              siteCompany: dispatch.deliverySiteCompanyId,
              site: dispatch.deliverySiteId,
              well: dispatch.deliveryWellId,
              container: dispatch.deliveryContainerId,
              category: dispatch.category,
            }
          }
        }
      }
    }

    navigate("/tickets/form", {state: state})
  }, [navigate, context])

  const onNewPickupTransport = useCallback(() => {
    let state: any = {
      type: transportType.pickup
    };

    const activity = firstOrUndefined(context)
    if (activity) {
      if (activity.shift) {
        const shift = activity.shift
        state = {
          ...state,
          options: {
            regionId: shift?.regionId,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(shift.startDttm),
            person: shift.personId,
            vehicleId: shift.vehicleId,
            trailer: shift.trailerId,
          }
        }
      } else if (activity.tta || activity.transport) {
        const pickupTransport = activity.tta?.pickupTransport ?? activity.pickupTransport

        state = {
          ...state,
          options: {
            regionId: pickupTransport?.regionId,
            category: pickupTransport?.category,
            categoryDisabled: typeof pickupTransport?.category !== "undefined"
          },
          formDefaults: toFormDefaultsFromTransport(pickupTransport),
        }
      } else if (activity.dispatch) {
        const dispatch = activity.dispatch
        state = {
          ...state,
          options: {
            regionId: dispatch.regionId,
            category: dispatch.category,
            categoryDisabled: true,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(dispatch.scheduleDttm),
            person: dispatch.personId,
            vehicleId: dispatch.vehicleId,
            trailer: dispatch.trailerId,
            siteCompany: dispatch.pickupSiteCompanyId,
            site: dispatch.pickupSiteId,
            well: dispatch.pickupWellId,
            container: dispatch.pickupContainerId,
          }
        }
      }
    }

    navigate("/transports/form", {state: {state}})
  }, [navigate, context])

  const onNewDeliveryTransport = useCallback(() => {
    let state: any = {
      type: transportType.delivery
    };

    const activity = firstOrUndefined(context)
    if (activity) {
      if (activity.shift) {
        const shift = activity.shift
        state = {
          ...state,
          options: {
            regionId: shift?.regionId,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(shift.startDttm),
            person: shift.personId,
            vehicleId: shift.vehicleId,
            trailer: shift.trailerId,
          }
        }
      } else if (activity.tta || activity.transport) {
        const deliveryTransport = activity.tta?.deliveryTransport ?? activity.deliveryTransport

        state = {
          ...state,
          options: {
            regionId: deliveryTransport?.regionId,
            category: deliveryTransport?.category,
            categoryDisabled: typeof deliveryTransport?.category !== "undefined"
          },
          formDefaults: toFormDefaultsFromTransport(deliveryTransport),
        }
      } else if (activity.dispatch) {
        const dispatch = activity.dispatch
        state = {
          ...state,
          options: {
            regionId: dispatch.regionId,
            category: dispatch.category,
            categoryDisabled: true,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(dispatch.scheduleDttm),
            person: dispatch.personId,
            vehicleId: dispatch.vehicleId,
            trailer: dispatch.trailerId,
            siteCompany: dispatch.pickupSiteCompanyId,
            site: dispatch.pickupSiteId,
            well: dispatch.pickupWellId,
            container: dispatch.pickupContainerId,
          }
        }
      }
    }

    navigate("/transports/form", {state: {state}})
  }, [navigate, context])

  const onNewDispatch = useCallback(() => {
    let state: any = {
      type: transportType.delivery
    };

    const activity = firstOrUndefined(context)
    if (activity) {
      if (activity.shift) {
        const shift = activity.shift
        state = {
          ...state,
          options: {
            regionId: shift?.regionId,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(shift.startDttm),
            company: shift.personCompanyId ?? shift.trailerCompanyId,
            person: shift.personId,
            vehicleId: shift.vehicleId,
            trailer: shift.trailerId,
          }
        }
      } else if (activity.tta || activity.transport) {
        const pickup = activity.tta?.pickupTransport ?? activity.pickupTransport
        const delivery = activity.tta?.deliveryTransport ?? activity.deliveryTransport

        const regionId = pickup?.regionId ?? delivery?.regionId
        const category = pickup?.category ?? delivery?.category
        const createDttm = pickup?.createDttm ?? delivery?.createDttm
        const personId = pickup?.personId ?? delivery?.personId
        const personCompanyId = pickup?.trailerCompanyId ?? delivery?.trailerCompanyId
        const vehicleId = pickup?.vehicleId ?? delivery?.vehicleId
        const trailerId = pickup?.trailerId ?? delivery?.trailerId
        const freightTypeId = pickup?.waterTypeId ?? delivery?.waterTypeId
        const serviceTypeId = pickup?.serviceTypeId ?? delivery?.serviceTypeId

        state = {
          ...state,
          options: {
            regionId: regionId,
            category: category,
            categoryDisabled: typeof category !== "undefined"
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(createDttm),
            company: personCompanyId,
            person: personId,
            vehicleId: vehicleId,
            trailer: trailerId,
            freightType: freightTypeId,
            serviceType: serviceTypeId,
            pickupSite: pickup?.siteId,
            pickupWell: pickup?.wellId,
            pickupContainer: pickup?.containerId,
            deliverySite: delivery?.siteId,
            deliveryWell: delivery?.wellId,
            deliveryContainer: delivery?.containerId,
          }
        }
      } else if (activity.dispatch) {
        const dispatch = activity.dispatch
        state = {
          ...state,
          options: {
            regionId: dispatch.regionId,
            category: dispatch.category,
            categoryDisabled: true,
          },
          formDefaults: {
            date: adjustFromTimeZoneSafely(dispatch.scheduleDttm),
            company: dispatch.personCompanyId ?? dispatch.trailerCompanyId,
            person: dispatch.personId,
            vehicleId: dispatch.vehicleId,
            trailer: dispatch.trailerId,
            pickup: {
              site: dispatch.pickupSiteId,
              well: dispatch.pickupWellId,
              container: dispatch.pickupContainerId,
            },
            delivery: {
              site: dispatch.deliverySiteId,
              well: dispatch.deliveryWellId,
              container: dispatch.deliveryContainerId,
            },
          }
        }
      }
    }

    navigate("/dispatch/form", {state: {state}})
  }, [navigate, context])

  return (
    <Menu
      width={260}
      position="bottom-end"
      transitionProps={{transition: "pop-top-right"}}
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton>
          <Group spacing={7}>
            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
              {t('common.action.plural', "Actions", {ns: "common"})}
            </Text>
            <IconChevronDown size={rem(12)} stroke={1.5}/>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={refetch}>
          {t("common.action.refresh", "Refresh", {ns: "common"})}
        </Menu.Item>
        <Menu.Divider/>
        <HasAccess permissions={["edit"]}>
          <Menu.Item onClick={onNewShift}>
            {t("common.action.new.shift", "New Shift", {ns: "common"})}
          </Menu.Item>
          <Menu.Item onClick={onNewTicket}>
            {t("common.action.new.tta.alt", "New Ticket", {ns: "common"})}
          </Menu.Item>
          <Menu.Item onClick={onNewPickupTransport}>
            {t("common.action.new.transport.pickup", "New Pickup Transport", {ns: "common"})}
          </Menu.Item>
          <Menu.Item onClick={onNewDeliveryTransport}>
            {t("common.action.new.transport.delivery", "New Delivery Transport", {ns: "common"})}
          </Menu.Item>
          <Menu.Item onClick={onNewDispatch}>
            {t("common.action.new.dispatch", "New Dispatch", {ns: "common"})}
          </Menu.Item>
          <DriverActivityTtaMenu context={context}/>
        </HasAccess>
      </Menu.Dropdown>
    </Menu>
  )
}
